import React, { useEffect, useMemo, useRef, useState } from "react";
import * as echarts from "echarts";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";

const formatUtcToBrowserDate = (time) => {
  if (!time) return null;

  const normalizedTime =
    typeof time === "string" && !time.endsWith("Z") ? `${time}Z` : time;

  return dayjs(normalizedTime);
};

const WirelessCongestionLevelSliderChart = ({
  data = [],
  defaultThreshold,
  externalThreshold,
  onThresholdChange,
}) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const [threshold, setThreshold] = useState(
    externalThreshold ?? defaultThreshold ?? 0,
  );

  const Y_AXIS_MAX = 90;
  const Y_AXIS_MIN = 0;

  const thresholdPercent =
    ((threshold - Y_AXIS_MIN) / (Y_AXIS_MAX - Y_AXIS_MIN)) * 100;

  const chartData = useMemo(() => {
    const series = Array.isArray(data) ? data : (data?.series ?? []);
    const dataMap = new Map();

    series.forEach((item) => {
      if (!Array.isArray(item)) return;

      const [time, value] = item;
      const browserTime = formatUtcToBrowserDate(time);

      if (!browserTime?.isValid()) return;

      const key = browserTime.format("YYYY-MM-DD HH");

      dataMap.set(key, Number(value) || 0);
    });

    const lastTime =
      series.length > 0
        ? formatUtcToBrowserDate(series[series.length - 1]?.[0])
        : dayjs().startOf("hour");

    const endTime =
      lastTime?.isValid() === true
        ? lastTime.startOf("hour")
        : dayjs().startOf("hour");

    const startTime = endTime.subtract(23, "hour");

    return Array.from({ length: 24 }, (_, index) => {
      const currentTime = startTime.add(index, "hour");
      const key = currentTime.format("YYYY-MM-DD HH");

      return {
        label: currentTime.format("HH:mm"),
        fullTime: currentTime.format("YYYY/MM/DD HH:mm"),
        value: dataMap.get(key) ?? 0,
      };
    });
  }, [data]);
  useEffect(() => {
    const value = externalThreshold ?? defaultThreshold ?? 0;

    setThreshold(value);

    if (onThresholdChange) {
      onThresholdChange(Number(value));
    }
  }, [externalThreshold, defaultThreshold, onThresholdChange]);

  useEffect(() => {
    if (!chartRef.current || chartData.length === 0) return;

    const chart = chartInstanceRef.current || echarts.init(chartRef.current);
    chartInstanceRef.current = chart;

    const xAxisData = chartData.map((item) => item.label);
    const yAxisData = chartData.map((item) => item.value);

    const option = {
      animation: false,
      backgroundColor: theme.palette.primary.white,
      grid: {
        top: 14,
        right: 18,
        bottom: 32,
        left: 42,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: theme.palette.primary.blackTransparency,
        borderWidth: 0,
        textStyle: {
          color: theme.palette.primary.white,
          fontSize: theme.typography.customFontSize[0],
        },
        formatter(params) {
          const point = params?.[0];
          if (!point) return "";
          return `${point.axisValue}<br/>Aps: ${point.data}`;
        },
        axisPointer: {
          type: "line",
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dashed",
          },
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: xAxisData,
        axisLine: {
          lineStyle: {
            color: theme.palette.primary.greyThread,
            width: 1,
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          margin: 12,
          interval: 0,
          formatter: (value, index) => {
            const isLast = index === xAxisData.length - 1;
            return index % 4 === 0 || isLast ? value : "";
          },
        },
        splitLine: {
          show: true,
          interval: 0,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dotted",
          },
        },
      },
      yAxis: {
        type: "value",
        min: Y_AXIS_MIN,
        max: Y_AXIS_MAX,
        interval: 30,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dotted",
          },
        },
      },
      series: [
        {
          type: "line",
          data: yAxisData,
          smooth: false,
          showSymbol: false,
          symbol: "none",
          lineStyle: {
            color: theme.palette.primary.orangeThird,
            width: 2,
          },
        },
      ],
    };

    chart.setOption(option, true);

    const handleResize = () => chart.resize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [chartData, theme]);

  useEffect(() => {
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div style={{ width: "100%" }}>
      <style>
        {`
          .threshold-slider {
            -webkit-appearance: none;
            width: 100%;
            background: transparent;
            outline: none;
          }

          .threshold-slider::-webkit-slider-runnable-track {
            width: 100%;
            height: 4px;
            cursor: pointer;
            background: transparent;
            border-radius: 2px;
          }

          .threshold-slider::-webkit-slider-thumb {
            height: 18px;
            width: 18px;
            border-radius: 50%;
            background: #FFFFFF;
            border: 3px solid #172664;
            cursor: pointer;
            -webkit-appearance: none;
            margin-top: -7px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          }

          .threshold-slider::-moz-range-track {
            width: 100%;
            height: 4px;
            background: transparent;
            border-radius: 2px;
          }

          .threshold-slider::-moz-range-thumb {
            height: 18px;
            width: 18px;
            border-radius: 50%;
            background: #FFFFFF;
            border: 3px solid #172664;
            cursor: pointer;
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
          }
        `}
      </style>

      <div
        ref={chartRef}
        style={{
          width: "100%",
          height: "320px",
        }}
      />

      <div
        style={{
          marginLeft: 31,
          marginRight: 31,
          marginTop: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 8,
            fontSize: 14,
            color: "#333333",
          }}
        >
          <div>
            Config: <strong>Client Capacity Threshold</strong>
          </div>

          <strong>{threshold}</strong>
        </div>

        <input
          type="range"
          className="threshold-slider"
          min={Y_AXIS_MIN}
          max={Y_AXIS_MAX}
          step="1"
          value={threshold}
          onChange={(e) => {
            const value = Number(e.target.value);

            setThreshold(value);

            if (onThresholdChange) {
              onThresholdChange(value);
            }
          }}
          style={{
            background: `linear-gradient(to right, #172664 0%, #172664 ${thresholdPercent}%, #E5E5EA ${thresholdPercent}%, #E5E5EA 100%)`,
            borderRadius: 2,
            height: 4,
          }}
        />
      </div>
    </div>
  );
};

export default WirelessCongestionLevelSliderChart;
